/**
 * @file        src/components/ConfirmHost.jsx
 * @description Renders the active themed confirm / prompt dialog (see lib/confirm).
 *              Mounted once at the app root next to <ToastHost />. Keyboard: Enter
 *              accepts, Esc cancels; clicking the backdrop cancels.
 * @author      Kinetika Labs
 * @created     2026-07-23
 * @changelog
 *   - 2026-07-23 Claude — Initial (replace native confirm/alert/prompt popups).
 */
import { useEffect, useState } from 'react';
import { useConfirmDialog } from '../lib/confirm';
import Icon from './Icon';

export default function ConfirmHost() {
  const dialog = useConfirmDialog((s) => s.dialog);
  const settle = useConfirmDialog((s) => s.settle);
  const [text, setText] = useState('');

  // Seed the prompt input each time a new dialog opens.
  useEffect(() => { setText(dialog?.defaultValue || ''); }, [dialog]);

  const isPrompt = dialog?.kind === 'prompt';

  useEffect(() => {
    if (!dialog) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') settle(isPrompt ? null : false);
      // Enter confirms for a plain confirm; the prompt handles Enter on its own input.
      else if (e.key === 'Enter' && !isPrompt) settle(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog, isPrompt, settle]);

  if (!dialog) return null;

  const cancel = () => settle(isPrompt ? null : false);
  const accept = () => settle(isPrompt ? text : true);

  return (
    <div
      className="lk-overlay confirm-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && cancel()}
    >
      <div className="lk-modal panel glass confirm-modal" role="alertdialog" aria-modal="true">
        <div className="lk-modal-head">
          <h3>{dialog.title || (isPrompt ? 'Enter a value' : 'Please confirm')}</h3>
          <button className="ib" aria-label="Close" onClick={cancel}><Icon name="x" /></button>
        </div>

        <div className="det-body">
          {dialog.message ? <p className="confirm-msg">{dialog.message}</p> : null}

          {isPrompt ? (
            <div className="field full">
              <input
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={dialog.placeholder || ''}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') accept();
                  if (e.key === 'Escape') cancel();
                }}
              />
            </div>
          ) : null}

          <div className="form-actions">
            <button className="btn ghost" onClick={cancel}>{dialog.cancelLabel || 'Cancel'}</button>
            <button
              className={`btn ${dialog.danger ? 'danger' : 'primary'}`}
              autoFocus={!isPrompt}
              onClick={accept}
            >
              {dialog.confirmLabel || 'OK'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
